<template>
  <div
    v-if="coords"
    ref="mapEl"
    data-testid="location-mention-map"
    class="h-80 w-full overflow-hidden rounded-md border border-gray-200 sm:h-96"
  />
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch, inject } from "vue"
import { storeToRefs } from "pinia"
import type { Location } from "@/models/location.model"
import type { LocationController } from "@/controllers/model-controllers/location-controller"
import { useGoogleMaps } from "@/composables/google-maps.ts"
import { isMediumMq } from "@/helper/app/device-helper"
import {
  createGoogleLocationPinContent,
  formatLocationMapPinLabel,
  forwardGeocode
} from "@/helper/location/location-helpers"
import { useAuthStore } from "@/store/auth.store"
import { Bus } from "@/services/bus"

const props = defineProps<{
  location: Location
}>()

const locationController = inject<LocationController>("locationController")
const { activeUser } = storeToRefs(useAuthStore())

const mapEl = ref<HTMLElement>()
const coords = ref<{ lat: number; lng: number } | null>(null)
const userCoords = ref<{ lat: number; lng: number } | null>(null)

const MAP_ID = "bcb83b04c4c522f9"

let map: google.maps.Map | null = null
let marker: google.maps.marker.AdvancedMarkerElement | null = null
let userMarker: google.maps.marker.AdvancedMarkerElement | null = null
let GoogleMarker: typeof google.maps.marker.AdvancedMarkerElement
let GooglePinElement: typeof google.maps.marker.PinElement

async function resolveCoords() {
  if (props.location.latitude != null && props.location.longitude != null) {
    coords.value = { lat: props.location.latitude, lng: props.location.longitude }
    return
  }

  const address = props.location.address?.trim()
  if (!address) return

  const apiKey = activeUser.value?.activeTenant?.tenantSettings?.apiKeyGoogleMaps ?? ""
  if (!apiKey) return

  const result = await forwardGeocode(address, apiKey)
  if (result) coords.value = result
}

async function fetchUserLocation() {
  if (!("geolocation" in navigator)) return

  try {
    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        maximumAge: 60_000,
        enableHighAccuracy: true,
        timeout: 10_000
      })
    })
    userCoords.value = {
      lat: position.coords.latitude,
      lng: position.coords.longitude
    }
  } catch {
    // User location is optional; ignore permission/unavailable errors.
  }
}

function fitMapToMarkers() {
  if (!map || !coords.value) return

  if (!userCoords.value) {
    map.panTo(coords.value)
    return
  }

  const bounds = new google.maps.LatLngBounds()
  bounds.extend(coords.value)
  bounds.extend(userCoords.value)
  map.fitBounds(bounds, { top: 48, right: 48, bottom: 48, left: 48 })
}

function renderUserMarker() {
  if (!map || !userCoords.value || !GoogleMarker || !GooglePinElement) return

  if (userMarker) {
    google.maps.event.clearInstanceListeners(userMarker)
    userMarker.map = null
  }

  userMarker = new GoogleMarker({
    map,
    position: userCoords.value,
    content: createGoogleLocationPinContent(
      GooglePinElement,
      "user-location-marker",
      "Je locatie",
      { background: "#3b82f6", borderColor: "#1d4ed8", glyphColor: "#ffffff" }
    ),
    gmpDraggable: false,
    zIndex: 10
  })
}

async function renderMap() {
  if (!mapEl.value || !coords.value) return

  if (!GoogleMarker) {
    const { importLibrary } = useGoogleMaps()
    const { Map } = await importLibrary("maps")
    const { AdvancedMarkerElement, PinElement } = await importLibrary("marker")
    GoogleMarker = AdvancedMarkerElement
    GooglePinElement = PinElement

    map = new Map(mapEl.value, {
      zoom: 15,
      center: coords.value,
      mapId: MAP_ID,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      streetViewControl: false,
      scaleControl: false,
      fullscreenControl: false,
      mapTypeControl: false,
      gestureHandling: isMediumMq() ? "cooperative" : "auto"
    })
  }

  if (marker) {
    google.maps.event.clearInstanceListeners(marker)
    marker.map = null
  }

  marker = new GoogleMarker({
    map,
    position: coords.value,
    content: createGoogleLocationPinContent(
      GooglePinElement,
      "map-marker",
      formatLocationMapPinLabel(props.location)
    ),
    gmpDraggable: false
  })

  fitMapToMarkers()
}

async function onInstructionTextChanged() {
  const id = props.location.id
  if (!id || !locationController) return
  try {
    const location = await locationController.detail(id)
    if (location?.latitude != null && location.longitude != null) {
      coords.value = { lat: location.latitude, lng: location.longitude }
    } else {
      await resolveCoords()
    }
    await renderMap()
  } catch {
    /* keep the current pin */
  }
}

watch(
  () => props.location,
  async () => {
    await resolveCoords()
    await renderMap()
  },
  { deep: true }
)

async function addUserLocationToMap() {
  await fetchUserLocation()
  renderUserMarker()
  fitMapToMarkers()
}

onMounted(async () => {
  await resolveCoords()
  await nextTick()
  await renderMap()
  void addUserLocationToMap()
  Bus.on("instruction-text-changed", onInstructionTextChanged)
})

onBeforeUnmount(() => {
  Bus.off("instruction-text-changed", onInstructionTextChanged)
  if (userMarker) {
    google.maps.event.clearInstanceListeners(userMarker)
    userMarker.map = null
    userMarker = null
  }
  if (marker) {
    google.maps.event.clearInstanceListeners(marker)
    marker.map = null
    marker = null
  }
  if (map) {
    google.maps.event.clearInstanceListeners(map)
    map = null
  }
})
</script>
